import {
  EffectOnTargetStatus,
  type MeiaCatalogThreatTargetSystemSelectDto,
  ScoringBand,
  StalenessStatus,
  type StudyRunCaseDashboardGateCardDto,
  StudyRunCaseDashboardGateStatus,
  type StudyRunCaseDashboardMissionDto,
  type StudyRunCaseDashboardStageHeatmapRowDto,
  type StudyRunCaseDashboardThreadVerdictDto,
  type StudyRunCaseSelectDto,
  type StudyScoringOutputSelectDto,
  type StudyTargetEffectOverrideSelectDto,
  type StudyTaskTargetEngagementSelectDto,
  type StudyTaskTechnologySelectDto,
  type StudyThreatTargetSelectDto,
  type StudyValidationResultDto,
} from '@@types/api.types';
import { formatEnumLabel } from '@utils/meia-enum.util';
import { type MeiaPkBand, buildTargetEffectRows, getPkBand } from './meia-p3-engagement.util';
import { scoreFromBand } from './meia-p4-run-cases.util';

// Maximum rows the Brief renders for paginated tables (traceability, validation log).
export const BRIEF_TABLE_ROW_LIMIT = 8;

export const briefSyncWindows = ['H-72', 'H-48', 'H-24', 'H-12', 'H-06', 'H-03', 'H-01', 'H-00', 'H+01', 'H+06'];

export type BriefRiskSeverity = 'H' | 'L' | 'M';

export type BriefRiskItem = {
  id: string;
  missionName?: string;
  mitigation: string;
  severity: BriefRiskSeverity;
  stageName?: string;
  threadName?: string;
  title: string;
  verdictBand?: ScoringBand | null;
};

export type BriefScopedStageRow = StudyRunCaseDashboardStageHeatmapRowDto & {
  missionId: string;
  missionName: string;
  scopedStageId: string;
  threadId: string;
  threadName: string;
};

export type BriefStudyVerdict = {
  band?: ScoringBand | null;
  score: number | null;
};

export type BriefSyncCell = {
  load: 'inactive' | 'normal' | 'overlap' | 'peak';
  window: string;
};

export type BriefSyncMatrixRow = {
  cells: BriefSyncCell[];
  stageId: string;
  stageName: string;
  verdictBand?: ScoringBand | null;
};

export type BriefTargetCoverageRow = {
  code: string;
  confidence: BriefTargetEffectConfidence;
  decisionMakerOverride?: StudyTargetEffectOverrideSelectDto;
  effectiveStatus: BriefTargetEffectStatus;
  engagementCount: number;
  label: string;
  maxPkEstimate: number | null;
  modelStatus: BriefTargetEffectStatus;
  technologyNames: string[];
};

export type BriefTargetEffectConfidence = MeiaPkBand;

export type BriefTargetEffectStatus = EffectOnTargetStatus;

export type BriefTargetEffectSummary = Record<BriefTargetEffectStatus, number>;

export type BriefThreadPhaseRollupRow = {
  missionName: string;
  stages: StudyRunCaseDashboardStageHeatmapRowDto[];
  threadId: string;
  threadName: string;
  threadVerdict?: StudyRunCaseDashboardThreadVerdictDto | null;
};

export type BriefWeakestStageDriver = {
  missionName: string;
  stageName: string;
  threadName: string;
} | null;

type BriefRecommendationParams = {
  activeMissionName?: string | null;
  activeThreadName?: string | null;
  band?: ScoringBand | null;
  mode?: 'aggregate' | 'selected';
  riskCount: number;
  totalStages?: number;
  totalTasks?: number;
  weakestStageName?: string | null;
};

type DefaultBriefRunCaseParams = {
  runCases: StudyRunCaseSelectDto[];
  scoringOutputs: StudyScoringOutputSelectDto[];
};

type TargetCoverageParams = {
  architectureAlternativeId: string | null;
  engagements: StudyTaskTargetEngagementSelectDto[];
  overrides: StudyTargetEffectOverrideSelectDto[];
  targetSystems: MeiaCatalogThreatTargetSystemSelectDto[];
  taskTechnologies: StudyTaskTechnologySelectDto[];
  threatTargets: StudyThreatTargetSelectDto[];
};

type BriefRiskParams = {
  gates: StudyRunCaseDashboardGateCardDto[];
  scoringOutput?: StudyScoringOutputSelectDto | null;
  stages: StudyRunCaseDashboardStageHeatmapRowDto[];
  validationResults: StudyValidationResultDto[];
};

type AggregateBriefRiskParams = {
  gates: StudyRunCaseDashboardGateCardDto[];
  missions: StudyRunCaseDashboardMissionDto[];
  scoringOutput?: StudyScoringOutputSelectDto | null;
  validationResults: StudyValidationResultDto[];
};

const PEAK_STAGE_IDS = new Set(['action', 'defeat', 'deliver', 'effect', 'engage', 'target']);

const getTime = (value: string): number => {
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
};

export const getDefaultBriefRunCaseId = (params: DefaultBriefRunCaseParams): string | null => {
  const { runCases, scoringOutputs } = params;
  const runCaseIds = new Set(runCases.map((runCase) => runCase.id));
  const currentOutputs = scoringOutputs
    .filter((output) => output.stalenessStatus === StalenessStatus.CURRENT && runCaseIds.has(output.runCaseId))
    .sort((a, b) => getTime(b.scoredAt) - getTime(a.scoredAt));

  const latestRunCase = [...runCases].sort(
    (a, b) =>
      Math.max(getTime(b.updatedAt), getTime(b.createdAt)) - Math.max(getTime(a.updatedAt), getTime(a.createdAt)),
  )[0];

  return currentOutputs[0]?.runCaseId ?? latestRunCase?.id ?? null;
};

export const getBriefStudyVerdict = (missions: StudyRunCaseDashboardMissionDto[]): BriefStudyVerdict => {
  let worst: BriefStudyVerdict = { band: null, score: null };

  for (const mission of missions) {
    for (const thread of mission.threadVerdicts) {
      const score = thread.score ?? scoreFromBand(thread.band);
      if (score === null) continue;
      if (worst.score === null || score < worst.score) {
        worst = {
          band: thread.band ?? null,
          score,
        };
      }
    }
  }

  return worst;
};

export const getBriefAggregateStats = (
  stages: StudyRunCaseDashboardStageHeatmapRowDto[],
): { greenStages: number; totalStages: number; totalTasks: number } => ({
  greenStages: stages.filter((stage) => stage.verdictBand === ScoringBand.GREEN).length,
  totalStages: stages.length,
  totalTasks: stages.reduce((total, stage) => total + stage.taskCount, 0),
});

export const getBriefScopedStageRows = (missions: StudyRunCaseDashboardMissionDto[]): BriefScopedStageRow[] =>
  missions.flatMap((mission) =>
    mission.threads.flatMap((thread) => {
      const threadVerdict = mission.threadVerdicts.find((verdict) => verdict.threadId === thread.threadId);
      const threadName = threadVerdict?.threadName ?? thread.threadId;

      return thread.stageHeatmap.map((stage) => ({
        ...stage,
        missionId: mission.missionId,
        missionName: mission.missionName,
        scopedStageId: `${thread.threadId}:${stage.stageId}`,
        threadId: thread.threadId,
        threadName,
      }));
    }),
  );

export const getBriefThreadPhaseRollupRows = (
  missions: StudyRunCaseDashboardMissionDto[],
): BriefThreadPhaseRollupRow[] =>
  missions.flatMap((mission) =>
    mission.threads.map((thread) => {
      const threadVerdict = mission.threadVerdicts.find((verdict) => verdict.threadId === thread.threadId) ?? null;

      return {
        missionName: mission.missionName,
        stages: thread.stageHeatmap,
        threadId: thread.threadId,
        threadName: threadVerdict?.threadName ?? thread.threadId,
        threadVerdict,
      };
    }),
  );

export const getBriefWeakestStageDriver = (stages: BriefScopedStageRow[]): BriefWeakestStageDriver => {
  const sortedStages = [...stages]
    .filter((stage) => stage.compositeScore !== null && stage.compositeScore !== undefined)
    .sort((a, b) => (a.compositeScore ?? Number.MAX_SAFE_INTEGER) - (b.compositeScore ?? Number.MAX_SAFE_INTEGER));
  const weakestStage = sortedStages[0] ?? stages[0];
  if (!weakestStage) return null;

  return {
    missionName: weakestStage.missionName,
    stageName: weakestStage.stageName,
    threadName: weakestStage.threadName,
  };
};

export const getSyncMatrixRows = (stages: StudyRunCaseDashboardStageHeatmapRowDto[]): BriefSyncMatrixRow[] => {
  const stageCount = stages.length || 1;
  const scale = briefSyncWindows.length / stageCount;

  return stages.map((stage, index) => {
    const start = Math.floor(index * scale);
    const end = Math.min(Math.floor((index + 1) * scale + 1), briefSyncWindows.length);
    const handoff = Math.max(start, Math.floor((index + 1) * scale) - 1);
    const normalizedStageId = stage.stageId.trim().toLowerCase();
    const isPeakStage =
      PEAK_STAGE_IDS.has(normalizedStageId) || PEAK_STAGE_IDS.has(stage.stageName.trim().toLowerCase());

    return {
      cells: briefSyncWindows.map((window, windowIndex) => ({
        load:
          windowIndex < start || windowIndex >= end
            ? 'inactive'
            : isPeakStage && ['H-03', 'H-01', 'H-00'].includes(window)
              ? 'peak'
              : windowIndex === handoff && index < stages.length - 1
                ? 'overlap'
                : 'normal',
        window,
      })),
      stageId: stage.stageId,
      stageName: stage.stageName,
      verdictBand: stage.verdictBand,
    };
  });
};

export const buildTargetCoverageRows = (params: TargetCoverageParams): BriefTargetCoverageRow[] => {
  const { architectureAlternativeId, engagements, overrides, targetSystems, taskTechnologies, threatTargets } = params;
  const targetEffectRows = buildTargetEffectRows({ architectureAlternativeId, engagements, overrides, targetSystems });
  const targetEffectRowsByCode = new Map(targetEffectRows.map((row) => [row.threatTargetSystemCode, row]));
  const targetSystemsByCode = new Map(targetSystems.map((targetSystem) => [targetSystem.id, targetSystem]));
  const threatTargetsByCode = new Map(
    threatTargets.map((target) => [normalizeCode(target.threatTargetSystemCode), target]),
  );
  const taskTechnologiesById = new Map(taskTechnologies.map((taskTechnology) => [taskTechnology.id, taskTechnology]));
  const allCodes = new Set<string>([
    ...targetEffectRows.map((row) => row.threatTargetSystemCode),
    ...threatTargets.map((target) => normalizeCode(target.threatTargetSystemCode)).filter(Boolean),
  ]);

  return Array.from(allCodes)
    .map((code) => {
      const targetEffectRow = targetEffectRowsByCode.get(code);
      const target = threatTargetsByCode.get(code);
      const targetSystem = targetSystemsByCode.get(code);
      const targetEngagements = engagements.filter((engagement) => engagement.threatTargetSystemCode === code);
      const modelStatus = targetEffectRow?.modelStatus ?? EffectOnTargetStatus.UNENGAGED;
      const decisionMakerOverride =
        targetEffectRow?.decisionMakerOverride ??
        overrides.find(
          (override) =>
            override.threatTargetSystemCode === code &&
            (override.architectureAlternativeId ?? null) === architectureAlternativeId,
        );

      return {
        code,
        confidence: getPkBand(targetEffectRow?.maxPkEstimate ?? null),
        decisionMakerOverride,
        effectiveStatus: decisionMakerOverride?.status ?? modelStatus,
        engagementCount: targetEffectRow?.engagementCount ?? 0,
        label: target?.alias?.trim() || targetSystem?.name || code,
        maxPkEstimate: targetEffectRow?.maxPkEstimate ?? null,
        modelStatus,
        technologyNames: uniqueSorted(
          targetEngagements.map(
            (engagement) =>
              taskTechnologiesById.get(engagement.studyTaskTechnologyId)?.technologyName ??
              taskTechnologiesById.get(engagement.studyTaskTechnologyId)?.technologyId ??
              '',
          ),
        ),
      };
    })
    .sort(compareTargetCoverageRows);
};

export const getBriefTargetEffectSummary = (rows: BriefTargetCoverageRow[]): BriefTargetEffectSummary => ({
  [EffectOnTargetStatus.CONTESTED]: rows.filter((row) => row.effectiveStatus === EffectOnTargetStatus.CONTESTED).length,
  [EffectOnTargetStatus.DEGRADED]: rows.filter((row) => row.effectiveStatus === EffectOnTargetStatus.DEGRADED).length,
  [EffectOnTargetStatus.SUPPRESSED]: rows.filter((row) => row.effectiveStatus === EffectOnTargetStatus.SUPPRESSED)
    .length,
  [EffectOnTargetStatus.UNENGAGED]: rows.filter((row) => row.effectiveStatus === EffectOnTargetStatus.UNENGAGED).length,
});

export const buildBriefRecommendation = (params: BriefRecommendationParams): string => {
  const { activeMissionName, activeThreadName, band, mode = 'selected', weakestStageName } = params;

  if (mode === 'aggregate') {
    if (band === ScoringBand.GREEN) {
      return 'Decision posture is green. Proceed to G4 review while monitoring residual assumptions.';
    }
    if (band === ScoringBand.YELLOW) {
      return 'Decision posture is conditional. Review open risks and validation findings before G4 approval.';
    }
    if (band === ScoringBand.RED) {
      return 'Decision posture is red. Return to P3/P4 to close task, measure, and scoring gaps before G4.';
    }

    return 'Run a scoring case before using this Brief for G4 review.';
  }

  const missionText = trimBriefLeadIn(activeMissionName) || 'Selected mission';
  const threadText = activeThreadName?.trim() || 'selected thread';
  const weakestStageText = weakestStageName?.trim() ? ` Weakest stage: ${weakestStageName}.` : '';

  if (band === ScoringBand.GREEN) {
    return `${missionText}: ${threadText} is green. Proceed to G4 review while monitoring residual assumptions.`;
  }
  if (band === ScoringBand.YELLOW) {
    return `${missionText}: ${threadText} is conditional.${weakestStageText} Review risks and validation findings before G4 approval.`;
  }
  if (band === ScoringBand.RED) {
    return `${missionText}: ${threadText} is red.${weakestStageText} Return to P3/P4 to close task, measure, and scoring gaps before G4.`;
  }

  return `${missionText}: run a scoring case before using this Brief for G4 review.`;
};

// Re-exported under the brief-domain name for backwards compat. New callers should import
// `formatEnumLabel` from `@utils/meia-enum.util` directly.
export const formatBriefEnumLabel = formatEnumLabel;

export const formatBriefValidationMessage = (message: string): string => {
  const withoutTaskId = message.replace(
    /^Task [0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12} technologies\[\d+\] \([0-9a-f-]{36}\) has no effective effectTypes: /i,
    'A task technology assignment has no effective effect types. ',
  );
  const withoutUuids = withoutTaskId
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, 'linked record')
    .replace(/\bMEIA technology snapshot row\b/g, 'technology snapshot')
    .replace(/\beffectTypesOverride\b/g, 'effect type override')
    .replace(/\beffectTypes\b/g, 'effect types')
    .replace(/\bEffect mapping\b/g, 'effect mapping')
    .replace(/\bthe technology snapshot\b/g, 'The technology snapshot')
    .replace(/\btask carries no effect type override\b/g, 'task has no effect type override')
    .replace(/\bEither author\b/g, 'Author');

  return withoutUuids.replace(/\s+/g, ' ').trim();
};

const trimBriefLeadIn = (value?: string | null): string => value?.trim().replace(/[.:!?]+$/, '') ?? '';

export const buildBriefRiskItems = (params: BriefRiskParams): BriefRiskItem[] => {
  const { gates, scoringOutput, stages, validationResults } = params;
  const risks: BriefRiskItem[] = [];

  for (const stage of stages) {
    if (stage.taskCount === 0) {
      risks.push({
        id: `stage-empty:${stage.stageId}`,
        mitigation: getStageRiskExplanation(stage),
        severity: 'H',
        stageName: stage.stageName,
        title: `${stage.stageName} stage has no Tasks`,
      });
    } else if (stage.verdictBand === ScoringBand.RED || stage.verdictBand === ScoringBand.YELLOW) {
      risks.push({
        id: `stage-verdict:${stage.stageId}`,
        mitigation: getStageRiskExplanation(stage),
        severity: stage.verdictBand === ScoringBand.RED ? 'H' : 'M',
        stageName: stage.stageName,
        title: `${stage.stageName} stage has a ${stage.verdictBand.toLowerCase()} verdict`,
        verdictBand: stage.verdictBand,
      });
    }
  }

  for (const gate of gates) {
    if (gate.status !== StudyRunCaseDashboardGateStatus.FAIL || gate.blockingCount === 0) continue;
    risks.push({
      id: `gate:${gate.gate}`,
      mitigation: gate.reason,
      severity: 'H',
      title: gate.detail,
    });
  }

  for (const result of validationResults) {
    if (!result.blocking) continue;
    risks.push({
      id: `validation:${result.ruleId}:${result.entityId}`,
      mitigation: result.megReference ?? 'Resolve the blocking validation finding before decision review.',
      severity: 'H',
      title: formatBriefValidationMessage(result.message),
    });
  }

  if (scoringOutput?.stalenessStatus === StalenessStatus.STALE) {
    risks.push({
      id: 'scoring-output:stale',
      mitigation: 'Rerun the selected run case so the Brief reflects current authoring data.',
      severity: 'M',
      title: 'Scoring output is stale',
    });
  }

  return risks;
};

export const buildAggregateBriefRiskItems = (params: AggregateBriefRiskParams): BriefRiskItem[] => {
  const scopedStageRisks: BriefRiskItem[] = [];

  for (const stage of getBriefScopedStageRows(params.missions)) {
    if (stage.taskCount === 0) {
      scopedStageRisks.push({
        id: `stage-empty:${stage.scopedStageId}`,
        missionName: stage.missionName,
        mitigation: getStageRiskExplanation(stage),
        severity: 'H',
        stageName: stage.stageName,
        threadName: stage.threadName,
        title: `${stage.stageName} stage of ${stage.threadName} has no Tasks`,
      });
    } else if (stage.verdictBand === ScoringBand.RED || stage.verdictBand === ScoringBand.YELLOW) {
      scopedStageRisks.push({
        id: `stage-verdict:${stage.scopedStageId}`,
        missionName: stage.missionName,
        mitigation: getStageRiskExplanation(stage),
        severity: stage.verdictBand === ScoringBand.RED ? 'H' : 'M',
        stageName: stage.stageName,
        threadName: stage.threadName,
        title: `${stage.stageName} stage of ${stage.threadName} has a ${stage.verdictBand.toLowerCase()} verdict`,
        verdictBand: stage.verdictBand,
      });
    }
  }

  return [
    ...scopedStageRisks,
    ...buildBriefRiskItems({
      gates: params.gates,
      scoringOutput: params.scoringOutput,
      stages: [],
      validationResults: params.validationResults,
    }),
  ];
};

const compareTargetCoverageRows = (a: BriefTargetCoverageRow, b: BriefTargetCoverageRow): number => {
  const statusOrder: Record<BriefTargetCoverageRow['effectiveStatus'], number> = {
    [EffectOnTargetStatus.SUPPRESSED]: 0,
    [EffectOnTargetStatus.DEGRADED]: 1,
    [EffectOnTargetStatus.CONTESTED]: 2,
    [EffectOnTargetStatus.UNENGAGED]: 3,
  };
  return statusOrder[a.effectiveStatus] - statusOrder[b.effectiveStatus] || a.code.localeCompare(b.code);
};

const normalizeCode = (value?: string | null): string => value?.trim() ?? '';

const uniqueSorted = (values: string[]): string[] =>
  Array.from(new Set(values.map((value) => value.trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));

const GREEN_SCORE_THRESHOLD = 90;
const YELLOW_SCORE_THRESHOLD = 50;

type StageScoreDimension = {
  label: string;
  value?: number | null;
};

const getStageRiskExplanation = (stage: StudyRunCaseDashboardStageHeatmapRowDto): string => {
  if (stage.taskCount === 0) {
    return 'No authored Tasks are available for this stage, so the scoring run cannot establish task, measure, or technology coverage.';
  }

  const impliedBand = getDashboardBand(stage.compositeScore);
  const weakDimensions = getWeakStageScoreDimensions(stage);
  const weakDimensionText =
    weakDimensions.length > 0
      ? ` Weakest inputs: ${formatStageScoreDimensions(weakDimensions)}.`
      : ' Score contributors are not available for every measure dimension.';

  if (stage.verdictBand === ScoringBand.RED) {
    const baseReason =
      impliedBand === ScoringBand.RED
        ? `The stage is red because its composite score is ${formatStageScore(stage.compositeScore)}, below the minimum ${YELLOW_SCORE_THRESHOLD}/100 threshold.`
        : `The stage is red because at least one linked task or measure has a red verdict, which takes precedence over the composite score of ${formatStageScore(stage.compositeScore)}.`;

    return `${baseReason}${weakDimensionText} ${stage.taskCount} task${stage.taskCount === 1 ? '' : 's'} contributed to this stage.`;
  }

  if (stage.verdictBand === ScoringBand.YELLOW) {
    const baseReason =
      impliedBand === ScoringBand.YELLOW
        ? `The stage is yellow because its composite score is ${formatStageScore(stage.compositeScore)}, clearing the minimum ${YELLOW_SCORE_THRESHOLD}/100 threshold but below the green ${GREEN_SCORE_THRESHOLD}/100 threshold.`
        : `The stage is yellow because at least one linked task or measure is limited, so the stage cannot clear the green ${GREEN_SCORE_THRESHOLD}/100 threshold.`;

    return `${baseReason}${weakDimensionText} ${stage.taskCount} task${stage.taskCount === 1 ? '' : 's'} contributed to this stage.`;
  }

  return `The stage has ${stage.verdictBand?.toLowerCase() ?? 'pending'} scoring with a composite score of ${formatStageScore(stage.compositeScore)} across ${stage.taskCount} task${stage.taskCount === 1 ? '' : 's'}.`;
};

const formatStageScore = (value?: number | null): string =>
  value === null || value === undefined ? 'no score' : `${value}/100`;

const formatStageScoreDimensions = (dimensions: StageScoreDimension[]): string =>
  dimensions.map((dimension) => `${dimension.label} ${formatStageScore(dimension.value)}`).join(', ');

const getDashboardBand = (score?: number | null): ScoringBand | null => {
  if (score === null || score === undefined) return null;
  if (score >= GREEN_SCORE_THRESHOLD) return ScoringBand.GREEN;
  if (score >= YELLOW_SCORE_THRESHOLD) return ScoringBand.YELLOW;
  return ScoringBand.RED;
};

const getWeakStageScoreDimensions = (stage: StudyRunCaseDashboardStageHeatmapRowDto): StageScoreDimension[] => {
  const dimensions: StageScoreDimension[] = [
    { label: 'MoS', value: stage.mosScore },
    { label: 'MoE', value: stage.moeScore },
    { label: 'MoP', value: stage.mopScore },
  ];
  const scoredDimensions = dimensions.filter((dimension) => dimension.value !== null && dimension.value !== undefined);
  const belowMinimumDimensions = scoredDimensions.filter(
    (dimension) => Number(dimension.value) < YELLOW_SCORE_THRESHOLD,
  );

  if (belowMinimumDimensions.length > 0) {
    return belowMinimumDimensions.sort(compareStageScoreDimensions);
  }

  return scoredDimensions
    .filter((dimension) => Number(dimension.value) < GREEN_SCORE_THRESHOLD)
    .sort(compareStageScoreDimensions);
};

const compareStageScoreDimensions = (a: StageScoreDimension, b: StageScoreDimension): number => {
  const leftValue = a.value ?? Number.MAX_SAFE_INTEGER;
  const rightValue = b.value ?? Number.MAX_SAFE_INTEGER;
  return leftValue - rightValue || a.label.localeCompare(b.label);
};
